/* eslint-disable react/no-unescaped-entities */
/**
 * /hustleup/messages  — DM inbox + active thread (cleartext HustleUp DMs).
 * /hustleup/messages?ct=<token> — magic-link consent landing.
 *
 * Architecture rules baked in:
 *  • First message from a stranger is gated; recipient sees a "pending"
 *    banner until they hit Accept (or Block) on the consent screen.
 *  • Block is bilateral. Sender's ban status auto-escalates via backend.
 *  • Banned senders see a banner with appeal CTA when they hit /messages.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Send, Mail, MessageSquare, Loader2, ShieldAlert, ShieldCheck,
  UserCheck, Slash, Search, Clock, Plus, ChevronRight, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });
const TKN = () => localStorage.getItem("hu_session_token");

const css = `
  .hd{min-height:100vh;background:#fffef5;color:#0f172a;font-family:Inter,sans-serif}
  .hd-top{display:flex;justify-content:space-between;align-items:center;padding:14px 18px;background:#0f172a;color:#fbbf24;position:sticky;top:0;z-index:30}
  .hd-back{color:#fbbf24;text-decoration:none;display:inline-flex;align-items:center;gap:6px;font-size:13px}
  .hd-main{max-width:680px;margin:0 auto;padding:18px;padding-bottom:120px}
  .hd-h1{font-family:Georgia,serif;font-size:28px;font-weight:800;margin:0 0 4px;letter-spacing:-0.025em}
  .hd-sub{color:#475569;font-size:13px;margin:0 0 18px;line-height:1.5;display:inline-flex;align-items:center;gap:6px}
  .hd-banner{padding:14px;border-radius:12px;margin:12px 0;display:flex;align-items:flex-start;gap:10px;font-size:13px;line-height:1.5}
  .hd-banner.warn{background:#fee2e2;color:#991b1b;border:1px solid #fecaca}
  .hd-banner.info{background:#fef3c7;color:#92400e;border:1px solid #fde68a}
  .hd-banner.ok{background:#dcfce7;color:#166534;border:1px solid #bbf7d0}
  .hd-row{display:flex;align-items:center;gap:14px;background:white;border:1px solid #e8e2d4;border-radius:14px;padding:14px;margin-bottom:10px;cursor:pointer;width:100%;text-align:left;font:inherit;color:inherit}
  .hd-row:hover{transform:translateY(-1px);box-shadow:0 8px 18px rgba(15,23,42,.05)}
  .hd-row.pending{border:1px solid #fde68a;background:#fffbeb}
  .hd-avatar{width:46px;height:46px;border-radius:50%;background:#fbbf24;color:#0f172a;display:inline-flex;align-items:center;justify-content:center;font-weight:800;font-size:18px;flex-shrink:0}
  .hd-name{font-weight:800;font-size:15px}
  .hd-meta{font-size:12px;color:#64748b}
  .hd-preview{font-size:13px;color:#475569;margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:340px}
  .hd-search{display:flex;align-items:center;gap:8px;background:white;border:1px solid #e8e2d4;border-radius:10px;padding:10px 12px;margin-bottom:14px}
  .hd-search input{border:none;outline:none;flex:1;font-size:14px;font-family:inherit;background:transparent}
  .hd-msg{max-width:75%;padding:10px 14px;border-radius:18px;margin-bottom:6px;font-size:14px;line-height:1.45;word-wrap:break-word}
  .hd-msg.in{background:white;border:1px solid #e8e2d4;border-bottom-left-radius:4px}
  .hd-msg.out{background:#0f172a;color:#fbbf24;align-self:flex-end;border-bottom-right-radius:4px;margin-left:auto}
  .hd-meta-row{display:flex;align-items:center;gap:6px;font-size:10px;color:#94a3b8;margin-bottom:8px}
  .hd-meta-row.out{justify-content:flex-end}
  .hd-input-bar{position:fixed;bottom:0;left:0;right:0;background:white;border-top:1px solid #e8e2d4;padding:12px 14px;display:flex;align-items:center;gap:10px;z-index:25}
  .hd-input-bar input{flex:1;padding:12px 16px;border:1px solid #e8e2d4;border-radius:999px;font-size:14px;outline:none;font-family:inherit}
  .hd-send-btn{background:#fbbf24;color:#0f172a;border:none;width:42px;height:42px;border-radius:50%;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;disabled:opacity:.5}
  .hd-fab{position:fixed;bottom:24px;right:24px;background:#0f172a;color:#fbbf24;border:none;width:54px;height:54px;border-radius:50%;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 12px 28px rgba(15,23,42,.25);z-index:24}
  .hd-empty{text-align:center;padding:60px 20px;color:#64748b}
  .hd-cta{display:inline-flex;align-items:center;gap:6px;background:#0f172a;color:#fbbf24;border:none;padding:10px 18px;border-radius:999px;font-weight:700;cursor:pointer;font-size:13px;text-decoration:none}
  .hd-cta.danger{background:#991b1b;color:white}
  .hd-cta.ghost{background:transparent;color:#0f172a;border:1px solid #e8e2d4}
  .spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}
`;

// ───────────────────────────────────────────────────────────────────
// /hustleup/messages  — Inbox + consent landing + thread overlay
// ───────────────────────────────────────────────────────────────────
export default function HustleUpMessagesPage() {
  const [params] = useSearchParams();
  const consentToken = params.get("ct");

  // Magic-link consent flow takes priority
  if (consentToken) return <ConsentLanding token={consentToken} />;

  return <InboxOrThread />;
}

function InboxOrThread() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [activeOtherId, setActiveOtherId] = useState(null);
  const [ban, setBan] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const t = TKN();
    if (!t) { navigate("/hustleup/join"); return; }
    try {
      const [ts, bn] = await Promise.all([
        api.get(`/api/hustleup/dm/threads?token=${t}`),
        api.get(`/api/hustleup/dm/me/ban?token=${t}`),
      ]);
      setThreads(ts.data || []);
      setBan(bn.data?.banned ? bn.data : null);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  // Search-as-you-type
  useEffect(() => {
    const t = TKN();
    if (!showNew || search.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/api/hustleup/dm/members/search`,
          { params: { q: search, token: t } });
        setResults(r.data || []);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(id);
  }, [search, showNew]);

  if (activeOtherId) return (
    <Thread otherId={activeOtherId}
            onBack={() => { setActiveOtherId(null); reload(); }} />
  );

  return (
    <div className="hd" data-testid="hd-inbox">
      <header className="hd-top">
        <a href="/hustleup" className="hd-back"><ArrowLeft size={14} /> HustleUp</a>
        <span style={{ fontWeight: 700, color: "white" }}>Messages</span>
      </header>

      <main className="hd-main">
        <h1 className="hd-h1">Your inbox</h1>
        <p className="hd-sub">
          <Mail size={12} /> HustleUp DMs · first messages need your OK
        </p>

        {ban && (
          <div className="hd-banner warn" data-testid="hd-ban-banner">
            <ShieldAlert size={18} />
            <div style={{ flex: 1 }}>
              <strong>You're banned from sending messages</strong>
              {ban.level === "permanent"
                ? " permanently. Submit an appeal — our AI Worker agent reviews within 48h."
                : ban.level === "t2"
                ? " for 60 days (Tier 2)."
                : " for 30 days (Tier 1)."}
              {ban.level === "permanent" && (
                <div style={{ marginTop: 10 }}>
                  <button className="hd-cta" onClick={() => navigate("/hustleup/messages/appeal")}
                          data-testid="hd-appeal-btn">
                    Submit appeal <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="hd-empty"><Loader2 className="spin" /> Loading…</div>
        ) : threads.length === 0 ? (
          <div className="hd-empty" data-testid="hd-empty">
            <MessageSquare size={32} color="#cbd5e1" />
            <p>No messages yet. Tap the + button to start one.</p>
          </div>
        ) : (
          threads.map(t => (
            <button key={t.id}
                    className={`hd-row ${t.pending_consent_for_me ? "pending" : ""}`}
                    onClick={() => setActiveOtherId(t.other.member_id)}
                    data-testid={`hd-thread-${t.id}`}>
              <div className="hd-avatar">
                {(t.other.first_name || "?")[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div className="hd-name">
                  {t.other.first_name} {t.other.last_name || ""}
                  {t.pending_consent_for_me && (
                    <span style={{ marginLeft: 8, fontSize: 10,
                                    background: "#fde68a", color: "#92400e",
                                    padding: "2px 8px", borderRadius: 999,
                                    fontWeight: 700, textTransform: "uppercase",
                                    letterSpacing: 0.5 }}>
                      Pending
                    </span>
                  )}
                </div>
                <div className="hd-preview">{t.last_preview || "—"}</div>
                <div className="hd-meta">
                  {t.last_at && new Date(t.last_at).toLocaleString("en-AU", {
                    weekday: "short", hour: "numeric", minute: "2-digit",
                  })}
                </div>
              </div>
              <ChevronRight size={16} color="#94a3b8" />
            </button>
          ))
        )}

        {showNew && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.6)",
                         display: "flex", alignItems: "center", justifyContent: "center",
                         zIndex: 40, padding: 20 }}
               onClick={() => setShowNew(false)}>
            <div onClick={e => e.stopPropagation()}
                 style={{ background: "white", borderRadius: 14, padding: 20,
                          width: "100%", maxWidth: 480 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontFamily: "Georgia, serif" }}>Start a new message</h3>
                <button onClick={() => setShowNew(false)}
                        style={{ background: "transparent", border: "none", cursor: "pointer" }}>
                  <X size={20} />
                </button>
              </div>
              <div className="hd-search">
                <Search size={14} color="#64748b" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                       placeholder="Search HustleUp members by name…"
                       data-testid="hd-new-search" />
              </div>
              {results.map(r => (
                <button key={r.member_id} className="hd-row"
                        onClick={async () => {
                          setActiveOtherId(r.member_id);
                          setShowNew(false); setSearch(""); setResults([]);
                        }}
                        data-testid={`hd-pick-${r.member_id}`}>
                  <div className="hd-avatar">{(r.first_name || "?")[0]?.toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div className="hd-name">{r.first_name} {r.last_name || ""}</div>
                    <div className="hd-meta">{r.state || "—"}</div>
                  </div>
                </button>
              ))}
              {search.length >= 2 && results.length === 0 && (
                <div className="hd-empty" style={{ padding: 20 }}>No matches.</div>
              )}
            </div>
          </div>
        )}
      </main>

      <button className="hd-fab" onClick={() => setShowNew(true)}
              data-testid="hd-fab" disabled={!!ban}>
        <Plus size={22} />
      </button>
      <style>{css}</style>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Thread view (selected from inbox)
// ───────────────────────────────────────────────────────────────────
function Thread({ otherId, onBack }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    const t = TKN();
    try {
      const r = await api.get(`/api/hustleup/dm/threads/${otherId}/messages?token=${t}`);
      setData(r.data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    } catch (e) {
      // Could be 404 if no thread yet — that's fine; we'll show "say hi"
      if (e?.response?.status === 404) {
        setData({ thread: null, pending_consent_for_me: false, messages: [] });
      } else {
        setErr(e?.response?.data?.detail || e.message);
      }
    }
  };

  useEffect(() => { load(); }, [otherId]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/api/hustleup/dm/send?token=${TKN()}`, {
        recipient_id: otherId, body: text,
      });
      setText("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const block = async () => {
    if (!window.confirm("Block this member? They won't be able to message you again. " +
                         "(Their lifetime block count will increment — repeat offenders are auto-banned.)")) return;
    try {
      await api.post(`/api/hustleup/dm/block?token=${TKN()}`,
        { sender_id: otherId });
      onBack();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const myId = data?.messages?.length
    ? data.messages.find(m => m.sender_id !== otherId)?.sender_id
    : null;

  return (
    <div className="hd" data-testid="hd-thread">
      <header className="hd-top">
        <button onClick={onBack} className="hd-back"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}>
          <ArrowLeft size={14} /> Back
        </button>
        <button onClick={block} className="hd-back" style={{ color: "#fca5a5",
                background: "transparent", border: "none", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 4 }}
                data-testid="hd-block-btn">
          <Slash size={12} /> Block
        </button>
      </header>
      <main className="hd-main" style={{ display: "flex", flexDirection: "column" }}>
        {data?.pending_consent_for_me && (
          <div className="hd-banner info">
            <Mail size={18} />
            <div>
              <strong>This is a first message.</strong> Sending a reply means
              you're happy to receive future messages from this person. You can
              also block them at any time.
            </div>
          </div>
        )}
        {err && <div className="hd-banner warn"><ShieldAlert size={16} /> {err}</div>}

        {(data?.messages || []).map(m => {
          const out = m.sender_id === myId;
          return (
            <div key={m.id} style={{ display: "flex", flexDirection: "column",
                                      alignSelf: out ? "flex-end" : "flex-start" }}>
              <div className={`hd-msg ${out ? "out" : "in"}`}>{m.body}</div>
              <div className={`hd-meta-row ${out ? "out" : ""}`}>
                {m.created_at && new Date(m.created_at).toLocaleTimeString(
                  "en-AU", { hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
          );
        })}
        {(!data?.messages || data.messages.length === 0) && (
          <div className="hd-empty">
            Send your first message. The recipient will receive an email
            and an in-platform alert; they'll need to OK before they reply.
          </div>
        )}
        <div ref={bottomRef} />
      </main>

      <div className="hd-input-bar">
        <input value={text} onChange={e => setText(e.target.value)}
               placeholder="Type a message…"
               onKeyDown={e => { if (e.key === "Enter") send(); }}
               data-testid="hd-thread-input" />
        <button className="hd-send-btn" onClick={send} disabled={busy}
                data-testid="hd-thread-send">
          {busy ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
        </button>
      </div>
      <style>{css}</style>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Magic-link consent landing
// ───────────────────────────────────────────────────────────────────
function ConsentLanding({ token }) {
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/hustleup/dm/consent/${token}`)
      .then(r => setPreview(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Link unavailable"));
  }, [token]);

  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/api/hustleup/dm/consent/${token}`, { action });
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="hd" data-testid="hd-consent">
      <header className="hd-top">
        <a href="/hustleup" className="hd-back"><ArrowLeft size={14} /> HustleUp</a>
        <span style={{ fontWeight: 700, color: "white" }}>Message request</span>
      </header>
      <main className="hd-main">
        {err && <div className="hd-banner warn"><ShieldAlert size={16} /> {err}</div>}

        {!preview && !err && (
          <div className="hd-empty"><Loader2 className="spin" /> Loading…</div>
        )}

        {preview && !done && (
          <>
            <h1 className="hd-h1">New message request</h1>
            <p className="hd-sub">
              <Clock size={12} /> Link expires{" "}
              {preview.expires_at && new Date(preview.expires_at).toLocaleString("en-AU")}
            </p>
            <div className="hd-banner info">
              <Mail size={18} />
              <div>
                <strong>{preview.sender.first_name} {preview.sender.last_name}</strong>
                {preview.sender.state ? ` from ${preview.sender.state}` : ""} wants
                to message you on HustleUp.
              </div>
            </div>
            <h3 style={{ fontFamily: "Georgia, serif", margin: "20px 0 8px" }}>
              Their first message
            </h3>
            <div style={{ background: "white", border: "1px solid #e8e2d4",
                           borderRadius: 12, padding: 16, marginBottom: 24,
                           fontSize: 14, lineHeight: 1.5 }}
                 data-testid="hd-consent-preview">
              {preview.first_message || "(no message preview)"}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="hd-cta" onClick={() => act("accept")} disabled={busy}
                      data-testid="hd-consent-accept">
                <UserCheck size={14} /> Accept & open chat
              </button>
              <button className="hd-cta danger" onClick={() => act("block")} disabled={busy}
                      data-testid="hd-consent-block">
                <Slash size={14} /> Block this sender
              </button>
            </div>
            <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 20 }}>
              Blocking is bilateral — they won't be able to send you any more messages
              and their lifetime block counter increments. Repeat offenders are
              auto-banned for 30 / 60 / permanent.
            </p>
          </>
        )}

        {done && (
          <div className="hd-banner ok">
            <ShieldCheck size={18} />
            <div>
              <strong>
                {done.action === "accepted" ? "Accepted." : "Blocked."}
              </strong>{" "}
              {done.action === "accepted"
                ? "Tap below to open the conversation."
                : "You'll never hear from this sender again."}
              <div style={{ marginTop: 10 }}>
                <a href="/hustleup/messages" className="hd-cta">
                  Open messages <ChevronRight size={14} />
                </a>
              </div>
            </div>
          </div>
        )}
      </main>
      <style>{css}</style>
    </div>
  );
}
